import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { z } from 'zod'
import {
  PermissionSchema,
  type UserAccessAccessSuccess,
  UserAccessAccessSuccessSchema,
  type UserAccessContext,
  UserAccessContextSchema,
  type UserDto
} from '~/services/api/generated'
import { UserRole } from '~/enums'
import { Normalization } from '~/utils/normalization'
import { parseVocabularyNames } from '~/utils/parseVocabularyNames'
import { normalizeIdentity } from '~/utils/normalizeIdentity'
import { resolveRoleNames } from '~/utils/resolveRoleNames'
import { identityGeneration } from '~/utils/identityGeneration'
import { hasTwoFactorEnabled } from '~/utils/hasTwoFactorEnabled'
import { hasText } from '~/utils/hasText'
import { findLifecycleOccurredAt } from '~/utils/findLifecycleOccurredAt'

const isRecord = Normalization.isRecord

// The generated UserAccess.context is `any` (lazy schema); the wire schema restores its type.
type UserAccess = Omit<UserAccessAccessSuccess['data'], 'context'> & { context: UserAccessContext }

const AccessWireSchema = UserAccessAccessSuccessSchema.extend({
  data: UserAccessAccessSuccessSchema.shape.data.extend({
    context: UserAccessContextSchema,
    permissions: z.array(z.string())
  })
})

const createDefaultAccess = (): UserAccess => ({
  capabilities: {
    can_manage_partner: false,
    can_manage_stores: false,
    can_use_consumer_portal: false
  },
  permissions: [],
  partner_onboarding_state: 'none',
  context: {
    partner: null,
    stores: []
  }
})

const getErrorMessage = (value: unknown): string =>
  Normalization.firstStringValue(
    'Failed to load user access details',
    isRecord(value) ? value['message'] : undefined
  ) ?? 'Failed to load user access details'

export const useUserStore = defineStore('user', () => {
  const user = ref<UserDto | null>(null)
  const access = ref<UserAccess>(createDefaultAccess())

  const loading = ref(false)
  const error = ref<string | null>(null)
  const initialized = ref(false)
  let accessRequest: Promise<void> | null = null
  const sanctumClient = useApiRequest()

  const logger = useLogger('UserStore')

  const parseAccess = (response: unknown): UserAccess | undefined => {
    const parsed = AccessWireSchema.safeParse(response)

    if (!parsed.success) {
      logger.error('accessContractMismatch', { issues: parsed.error.issues })
      return undefined
    }

    return {
      ...parsed.data.data,
      permissions: parseVocabularyNames(
        PermissionSchema,
        parsed.data.data.permissions,
        logger,
        'permissionOutsideVocabulary'
      )
    }
  }

  const resolveSanctumUser = (value: unknown): UserDto | null => normalizeIdentity(value, logger)

  // useIdentity() is the source of truth for identity; the Pinia user is
  // only synced during fetchUserAccess(), which doesn't run on every route.
  const { identity: sanctumIdentity } = useIdentity()

  const resolveUser = (): UserDto | null => resolveSanctumUser(sanctumIdentity.value) ?? user.value

  const syncUserFromSanctum = () => {
    const sanctumUser = resolveSanctumUser(sanctumIdentity.value)
    if (sanctumUser) user.value = sanctumUser
  }

  const hasPhone = computed(() => hasText(resolveUser()?.phone))

  // A user with no phone has nothing to verify.
  const isPhoneVerified = computed(() => {
    if (!hasPhone.value) return true
    return findLifecycleOccurredAt(resolveUser(), 'phone_verified') !== undefined
  })

  const isTwoFactorEnabled = computed(() => hasTwoFactorEnabled(resolveUser()))

  const hasPartnerAccess = computed(() => !!access.value?.capabilities?.can_manage_partner)
  const hasStoreAccess = computed(() => !!access.value?.capabilities?.can_manage_stores)
  const hasConsumerAccess = computed(() => !!access.value?.capabilities?.can_use_consumer_portal)
  // Internal staff (admin/super_admin) eligible for a time-boxed partner-access grant (APP-1688)
  const isInternalStaff = computed(() =>
    resolveRoleNames(resolveUser()).some(
      (role) => role === UserRole.ADMIN || role === UserRole.SUPER_ADMIN
    )
  )
  // Organizer account type (from UserDto: is_organizer / organizer_accounts)
  const isOrganizer = computed(() => resolveUser()?.is_organizer === true)
  const organizerAccount = computed(() => resolveUser()?.organizer_accounts[0])
  const partnerContext = computed(() => access.value.context?.partner ?? null)
  // True once brand setup has created the partner; `can_manage_partner` is already true for a partner-less owner.
  const hasPartner = computed(() => partnerContext.value !== null)
  const partnerOnboardingState = computed(() => access.value.partner_onboarding_state)
  const storeContexts = computed(() => access.value.context?.stores ?? [])

  const updateUser = (userData: UserDto) => {
    user.value = { ...userData }
  }

  // A failed refetch keeps the access already loaded; only a first load fails closed to no access.
  const failAccess = (message: string) => {
    error.value = message
    if (!initialized.value) access.value = createDefaultAccess()
  }

  // The access payload never carries a `user`; identity comes from Sanctum's /api/user.
  const applyAccess = (response: unknown): void => {
    syncUserFromSanctum()

    const parsedAccess = parseAccess(response)

    if (parsedAccess === undefined) {
      failAccess('Failed to load user access details')
      return
    }

    access.value = parsedAccess
    initialized.value = true
  }

  const requestUserAccess = async (): Promise<void> => {
    const generation = identityGeneration.current()
    loading.value = true
    error.value = null

    try {
      const response = await sanctumClient('/api/user/access')
      if (!identityGeneration.isCurrent(generation)) return

      applyAccess(response)
    } catch (err) {
      if (!identityGeneration.isCurrent(generation)) return
      // Do not clear user.value — Sanctum login data is still valid
      failAccess(getErrorMessage(err))
    } finally {
      if (identityGeneration.isCurrent(generation)) loading.value = false
    }
  }

  const fetchUserAccess = async (force = false): Promise<void> => {
    if (initialized.value && !force) {
      return
    }

    if (accessRequest !== null) {
      const inFlight = accessRequest
      await inFlight
      if (!force) return
      // A forced refresh must read access fetched after it was requested; the request it waited on
      // began earlier. A request another forced caller started meanwhile is newer, so it is shared.
      if (accessRequest !== null && accessRequest !== inFlight) {
        await accessRequest
        return
      }
    }

    const generation = identityGeneration.current()
    accessRequest = requestUserAccess()
    try {
      await accessRequest
    } finally {
      if (identityGeneration.isCurrent(generation)) accessRequest = null
    }
  }

  const clearUserState = () => {
    user.value = null
    access.value = createDefaultAccess()
    initialized.value = false
    error.value = null
    // Owns the spinner too: a superseded request leaves its own `finally` write unmade.
    loading.value = false
    // A new identity must not be served by the previous one's in-flight request.
    accessRequest = null
  }

  return {
    user,
    access,
    loading,
    error,
    initialized,
    hasPhone,
    isPhoneVerified,
    isTwoFactorEnabled,
    syncUserFromSanctum,
    hasPartnerAccess,
    hasStoreAccess,
    hasConsumerAccess,
    isInternalStaff,
    isOrganizer,
    organizerAccount,
    partnerContext,
    hasPartner,
    partnerOnboardingState,
    storeContexts,
    updateUser,
    fetchUserAccess,
    clearUserState
  }
})
